import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';
import type {
  WorkflowPublishedWorkFact,
  WorkflowPublishedWorkDetailRow,
  WorkflowPublishedWorkDetailGroup,
  WorkflowPublishedWorkRow,
  WorkflowPublishedWorkSection,
} from './workflow-published-work-inspection';
export type WorkflowPublishedWorkflowSettingsSectionId =
  | 'publicReferences'
  | 'eventAutomation'
  | 'dependencies'
  | 'navigation';
export type WorkflowPublishedWorkflowSettingsFact = WorkflowPublishedWorkFact;
export type WorkflowPublishedWorkflowSettingsDetailRow =
  WorkflowPublishedWorkDetailRow;
export type WorkflowPublishedWorkflowSettingsDetailGroup =
  WorkflowPublishedWorkDetailGroup;
export type WorkflowPublishedWorkflowSettingsRow = WorkflowPublishedWorkRow;
export interface WorkflowPublishedWorkflowSettingsSection extends Omit<
  WorkflowPublishedWorkSection,
  'id'
> {
  id: WorkflowPublishedWorkflowSettingsSectionId;
}
export interface WorkflowPublishedWorkflowSettingsInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  issues: readonly string[];
  originalDocument: unknown | null;
  sections: Readonly<
    Record<
      WorkflowPublishedWorkflowSettingsSectionId,
      WorkflowPublishedWorkflowSettingsSection
    >
  >;
}
type Saved = Record<string, unknown>;
type Field = readonly [string, string];
const record = (raw: unknown): raw is Saved =>
  !!raw && typeof raw === 'object' && !Array.isArray(raw);
const object = (raw: unknown): Saved => (record(raw) ? raw : {});
const presence = (owner: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(owner, key)
    ? 'absent'
    : owner[key] === null
      ? 'null'
      : 'value';
const copy = (raw: unknown): unknown => structuredClone(raw);
const text = (raw: unknown): raw is string =>
  typeof raw === 'string' && !!raw.trim();
function display(raw: unknown): string {
  if (raw === undefined) return 'Not declared';
  if (raw === null) return 'Explicitly null';
  if (typeof raw === 'string') return raw === '' ? 'Empty saved text' : raw;
  try {
    return JSON.stringify(raw) ?? 'Unsupported saved value';
  } catch {
    return 'Unsupported saved value';
  }
}
function facts(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedWorkflowSettingsFact[] {
  const value = object(raw);
  return fields.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    presence: presence(value, key),
    value: display(value[key]),
    issues: [],
    raw: copy(value[key]),
  }));
}
function extraIssues(raw: unknown, fields: readonly string[]): string[] {
  return Object.keys(object(raw)).some((key) => !fields.includes(key))
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function duplicateIssues(
  raw: unknown,
  index: number,
  all: readonly unknown[],
  key: string,
  label: string,
): string[] {
  const value = object(raw)[key];
  if (!text(value))
    return [
      `Saved ${label} at index ${index} is missing or has an unsupported shape.`,
    ];
  return all.filter((item) => object(item)[key] === value).length === 1
    ? []
    : [
        `Duplicate saved ${label}: ${value}. Every indexed declaration is retained independently.`,
      ];
}
function detail(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedWorkflowSettingsDetailRow {
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: facts(raw, fields, `${prefix}-${index}`),
    issues: [
      ...(!record(raw)
        ? [
            'This saved entry has an unsupported shape. Its original value is retained.',
          ]
        : []),
      ...issues,
    ],
    raw: copy(raw),
  };
}
function objectGroup(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedWorkflowSettingsDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key);
  const rows =
    savedPresence === 'value'
      ? [
          detail(raw, 0, `${prefix}:${key}`, label, fields, [
            ...issues,
            ...extraIssues(
              raw,
              fields.map(([name]) => name),
            ),
          ]),
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : 'available',
    rows,
    issues: [],
    raw: copy(raw),
  };
}
function collection(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (
    raw: unknown,
    index: number,
    all: readonly unknown[],
  ) => WorkflowPublishedWorkflowSettingsDetailRow,
): WorkflowPublishedWorkflowSettingsDetailGroup {
  const raw = owner[key],
    savedPresence = presence(owner, key),
    rows = Array.isArray(raw)
      ? raw.map((item, index) => project(item, index, raw))
      : [];
  const issues =
    savedPresence === 'value' && !Array.isArray(raw)
      ? [
          'This saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence: savedPresence,
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.issues.length)
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}
function strings(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
): WorkflowPublishedWorkflowSettingsDetailGroup {
  return collection(owner, key, label, prefix, (raw, index, all) => ({
    id: `${prefix}:${key}-${index}`,
    index,
    label: `Saved value ${index + 1}`,
    fields: [
      {
        id: `${prefix}:${key}-${index}:value`,
        label: 'Value',
        presence: 'value',
        value: display(raw),
        issues: [],
        raw: copy(raw),
      },
    ],
    issues: [
      ...(!text(raw) ? ['This saved value is not nonempty text.'] : []),
      ...(text(raw) && all.filter((item) => item === raw).length > 1
        ? [
            `Duplicate saved value: ${raw}. Its order and occurrences are retained.`,
          ]
        : []),
    ],
    raw: copy(raw),
  }));
}
const labels: Record<WorkflowPublishedWorkflowSettingsSectionId, string> = {
  publicReferences: 'Public references',
  eventAutomation: 'Event automation',
  dependencies: 'Modules and dependencies',
  navigation: 'Staff navigation',
};
const referenceFields: readonly Field[] = [
  ['key', 'Capability key'],
  ['inputSchemaHash', 'Input schema hash'],
  ['outputSchemaHash', 'Output schema hash'],
  ['contractHash', 'Contract hash'],
];
function referenceKey(raw: unknown): string | null {
  const key = object(object(raw)['key']);
  return text(key['provider']) &&
    text(key['kind']) &&
    text(key['code']) &&
    typeof key['version'] === 'number' &&
    Number.isSafeInteger(key['version']) &&
    key['version'] > 0
    ? JSON.stringify([
        key['provider'],
        key['kind'],
        key['code'],
        key['version'],
      ])
    : null;
}
function reference(
  raw: unknown,
  index: number,
  prefix: string,
): WorkflowPublishedWorkflowSettingsDetailRow {
  const value = object(raw),
    key = object(value['key']);
  const row = detail(
    raw,
    index,
    prefix,
    `Saved capability ${index + 1}`,
    referenceFields,
    [
      ...(!referenceKey(raw) ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((name) =>
        text(value[name]),
      )
        ? [
            'The saved capability reference is incomplete or unsupported. No current reference is substituted.',
          ]
        : []),
      ...extraIssues(key, ['provider', 'kind', 'code', 'version']),
    ],
  );
  row.fields = [
    ...facts(
      key,
      [
        ['provider', 'Provider'],
        ['kind', 'Kind'],
        ['code', 'Code'],
        ['version', 'Version'],
      ],
      `${prefix}-${index}:key`,
    ),
    ...row.fields,
  ];
  return row;
}
function commandGroup(
  action: Saved,
  prefix: string,
  document: Saved,
): WorkflowPublishedWorkflowSettingsDetailGroup {
  const raw = action['command'],
    savedPresence = presence(action, 'command'),
    pins = document['capabilityReferences'];
  const rows =
    savedPresence === 'value' ? [reference(raw, 0, `${prefix}:command`)] : [];
  const issues =
    savedPresence !== 'value'
      ? [
          'A full command reference is not recorded; no current command is inferred.',
        ]
      : [];
  if (rows.length) {
    const key = referenceKey(raw),
      matches = Array.isArray(pins)
        ? pins.filter((pin) => key !== null && referenceKey(pin) === key)
        : [];
    if (
      matches.length !== 1 ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(
        (name) => object(matches[0])[name] === object(raw)[name],
      )
    )
      rows[0].issues = [
        ...rows[0].issues,
        'One exact saved command pin is not established. Current installation and execution support are not evaluated.',
      ];
    if (object(object(raw)['key'])['kind'] !== 'command')
      rows[0].issues = [
        ...rows[0].issues,
        'The saved action does not identify a command capability. It is retained without reinterpretation.',
      ];
  }
  return {
    id: `${prefix}:command`,
    label: 'Saved exact action command',
    presence: savedPresence,
    rows,
    issues,
    raw: copy(raw),
    state:
      issues.length || rows.some((row) => row.issues.length)
        ? 'retained-unknown'
        : 'available',
  };
}
function row(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  groups: readonly WorkflowPublishedWorkflowSettingsDetailGroup[] = [],
  issues: readonly string[] = [],
): WorkflowPublishedWorkflowSettingsRow {
  const base = detail(raw, index, prefix, label, fields, issues);
  return {
    ...base,
    description:
      'Saved workflow configuration only. No policy lookup, event dispatch, installed module availability or runtime authority is inferred.',
    groups,
    state:
      base.issues.length ||
      groups.some((group) => group.state === 'retained-unknown')
        ? 'retained-unknown'
        : 'available',
  };
}
function section(
  document: Saved,
  key: string,
  id: WorkflowPublishedWorkflowSettingsSectionId,
  rows: WorkflowPublishedWorkflowSettingsRow[],
  issues: readonly string[] = [],
): WorkflowPublishedWorkflowSettingsSection {
  const raw = document[key],
    savedPresence = presence(document, key);
  return {
    id,
    label: labels[id],
    presence: savedPresence,
    rows,
    issues,
    raw: copy(raw),
    state:
      savedPresence !== 'value'
        ? 'absent'
        : issues.length || rows.some((row) => row.state === 'retained-unknown')
          ? 'retained-unknown'
          : Array.isArray(raw) && !raw.length
            ? 'empty'
            : 'available',
  };
}
function publicReferences(
  document: Saved,
): WorkflowPublishedWorkflowSettingsSection {
  const raw = document['numbering'],
    value = object(raw),
    fields: readonly Field[] = [
      ['mode', 'Saved reference mode'],
      ['policyId', 'Saved policy ID'],
      ['agencyCode', 'Agency code'],
      ['programCode', 'Program code'],
    ];
  const rows =
    presence(document, 'numbering') === 'value'
      ? [
          row(
            raw,
            0,
            'numbering',
            'Saved public reference settings',
            fields,
            [],
            [
              ...(value['mode'] !== 'disabled' && value['mode'] !== 'required'
                ? [
                    'The saved reference mode is unknown. No mode or policy is selected.',
                  ]
                : []),
              ...(['policyId', 'agencyCode', 'programCode'].some(
                (key) => value[key] != null && typeof value[key] !== 'string',
              )
                ? [
                    'An optional saved reference value has an unsupported shape. Its original value is retained.',
                  ]
                : []),
            ],
          ),
        ]
      : [];
  return section(document, 'numbering', 'publicReferences', rows);
}
const actionFields: readonly Field[] = [
  ['id', 'Action ID'],
  ['typeCode', 'Action type'],
  ['group', 'Saved group'],
  ['order', 'Saved order'],
  ['mode', 'Execution mode'],
  ['failurePolicy', 'Failure policy'],
  ['caller', 'Caller'],
  ['requiredForProgression', 'Required for progression'],
];
function actionGroups(
  trigger: Saved,
  prefix: string,
  document: Saved,
): WorkflowPublishedWorkflowSettingsDetailGroup[] {
  const actions = trigger['actions'];
  const metadata = collection(
    trigger,
    'actions',
    'Saved ordered actions',
    prefix,
    (raw, index, all) => {
      const value = object(raw),
        current = detail(
          raw,
          index,
          `${prefix}:action`,
          text(value['id'])
            ? display(value['id'])
            : `Saved action ${index + 1}`,
          [
            ...actionFields,
            ['command', 'Command'],
            ['inputs', 'Inputs'],
            ['outcomes', 'Outcomes'],
          ],
          [
            ...duplicateIssues(raw, index, all, 'id', 'action ID'),
            ...(['typeCode', 'group', 'mode', 'failurePolicy', 'caller'].some(
              (key) => !text(value[key]),
            ) ||
            typeof value['order'] !== 'number' ||
            !Number.isSafeInteger(value['order']) ||
            typeof value['requiredForProgression'] !== 'boolean'
              ? [
                  'Some saved action policy fields are unsupported. No historical defaults or execution mode are supplied.',
                ]
              : []),
          ],
        );
      current.fields = facts(raw, actionFields, `${prefix}:action-${index}`);
      return current;
    },
  );
  const groups: WorkflowPublishedWorkflowSettingsDetailGroup[] = [metadata];
  if (Array.isArray(actions))
    actions.forEach((raw, index) => {
      const action = object(raw),
        actionPrefix = `${prefix}:action-${index}`,
        firstGroup = groups.length;
      groups.push(commandGroup(action, actionPrefix, document));
      groups.push(
        collection(
          action,
          'inputs',
          'Saved action inputs',
          actionPrefix,
          (item, inputIndex, all) =>
            detail(
              item,
              inputIndex,
              `${actionPrefix}:input`,
              `Saved input ${inputIndex + 1}`,
              [
                ['field', 'Field'],
                ['source', 'Source'],
                ['value', 'Saved literal value'],
                ['context', 'Context field'],
                ['literalJson', 'Saved literal JSON'],
              ],
              duplicateIssues(item, inputIndex, all, 'field', 'input field'),
            ),
        ),
      );
      groups.push(
        strings(action, 'outcomes', 'Saved action outcomes', actionPrefix),
      );
      for (const group of groups.slice(firstGroup))
        group.label = `Saved action ${index + 1} · ID ${display(action['id'])}: ${group.label}`;
    });
  return groups;
}
function uniqueTarget(
  collection: unknown,
  id: unknown,
): { raw: Saved; index: number } | null {
  if (!Array.isArray(collection) || !text(id)) return null;
  const matches = collection
    .map((raw, index) => ({ raw, index }))
    .filter((item) => object(item.raw)['id'] === id);
  return matches.length === 1 && record(matches[0].raw)
    ? { raw: matches[0].raw, index: matches[0].index }
    : null;
}
function targetFacts(
  prefix: string,
  label: string,
  target: ReturnType<typeof uniqueTarget>,
): WorkflowPublishedWorkflowSettingsFact[] {
  return target
    ? [
        {
          id: `${prefix}:index`,
          label: `Saved ${label} index`,
          presence: 'value',
          value: String(target.index),
          issues: [],
          raw: target.index,
        },
        {
          id: `${prefix}:target`,
          label: `Unique saved ${label}`,
          presence: 'value',
          value: display(target.raw['displayName'] ?? target.raw['id']),
          issues: [],
          raw: copy(target.raw),
        },
      ]
    : [];
}
function eventAutomation(
  document: Saved,
): WorkflowPublishedWorkflowSettingsSection {
  const triggers = document['triggers'],
    fields: readonly Field[] = [
      ['id', 'Trigger ID'],
      ['kind', 'Saved event kind'],
      ['nodeId', 'Saved node ID'],
      ['edgeId', 'Saved edge ID'],
      ['outcomeCode', 'Saved outcome code'],
    ];
  const rows = Array.isArray(triggers)
    ? triggers.map((raw, index) => {
        const value = object(raw),
          prefix = `trigger-${index}`,
          node = uniqueTarget(document['nodes'], value['nodeId']),
          edge = uniqueTarget(document['edges'], value['edgeId']);
        const current = row(
          raw,
          index,
          'trigger',
          text(value['id'])
            ? display(value['id'])
            : `Saved trigger ${index + 1}`,
          [...fields, ['actions', 'Actions']],
          actionGroups(value, prefix, document),
          [
            ...duplicateIssues(raw, index, triggers, 'id', 'trigger ID'),
            ...(!text(value['kind'])
              ? [
                  'The saved event kind is unsupported. No dispatch support is inferred.',
                ]
              : []),
            ...(!node
              ? [
                  'The saved node ID does not identify one node. No event owner is substituted.',
                ]
              : []),
            ...(value['edgeId'] != null && !edge
              ? [
                  'The saved edge ID does not identify one edge. No path is substituted.',
                ]
              : []),
            ...(['edgeId', 'outcomeCode'].some(
              (key) => value[key] != null && typeof value[key] !== 'string',
            )
              ? ['An optional saved event reference has an unsupported shape.']
              : []),
          ],
        );
        current.fields = [
          ...facts(raw, fields, prefix),
          ...targetFacts(`${prefix}:node`, 'node', node),
          ...targetFacts(`${prefix}:edge`, 'edge', edge),
        ];
        return current;
      })
    : [];
  return section(
    document,
    'triggers',
    'eventAutomation',
    rows,
    presence(document, 'triggers') === 'value' && !Array.isArray(triggers)
      ? [
          'The saved trigger collection is opaque. Its original value is retained without inventing events.',
        ]
      : [],
  );
}
function dependencies(
  document: Saved,
): WorkflowPublishedWorkflowSettingsSection {
  const raw: Saved = {};
  for (const key of ['packReference', 'capabilityReferences'])
    if (Object.hasOwn(document, key)) raw[key] = copy(document[key]);
  if (!Object.keys(raw).length)
    return {
      id: 'dependencies',
      label: labels.dependencies,
      state: 'absent',
      presence: 'absent',
      rows: [],
      issues: [],
      raw: undefined,
    };
  const pack = objectGroup(
    document,
    'packReference',
    'Saved exact pack reference',
    'dependencies',
    [
      ['code', 'Pack code'],
      ['version', 'Pack version'],
      ['manifestHash', 'Saved manifest hash'],
    ],
  );
  const packValue = object(document['packReference']);
  if (
    pack.rows.length &&
    (!text(packValue['code']) ||
      typeof packValue['version'] !== 'number' ||
      !Number.isSafeInteger(packValue['version']) ||
      packValue['version'] < 1 ||
      !text(packValue['manifestHash']))
  ) {
    pack.rows[0].issues = [
      ...pack.rows[0].issues,
      'The saved pack reference is incomplete or unsupported. No installed pack or dependency closure is substituted.',
    ];
    pack.state = 'retained-unknown';
  }
  const pins = collection(
    document,
    'capabilityReferences',
    'All saved capability references',
    'dependencies',
    (item, index, all) => {
      const current = reference(item, index, 'saved-pin'),
        key = referenceKey(item);
      if (
        key &&
        all.filter((candidate) => referenceKey(candidate) === key).length > 1
      )
        current.issues = [
          ...current.issues,
          'Duplicate saved capability key. All exact references and hashes remain in their original order.',
        ];
      return current;
    },
  );
  const current = row(
    raw,
    0,
    'dependencies',
    'Saved pack and capability references',
    [
      ['packReference', 'Pack reference'],
      ['capabilityReferences', 'Capability references'],
    ],
    [pack, pins],
  );
  current.fields = [];
  return {
    id: 'dependencies',
    label: labels.dependencies,
    presence: 'value',
    state: current.state,
    rows: [current],
    issues: [],
    raw: copy(raw),
  };
}
function navigation(document: Saved): WorkflowPublishedWorkflowSettingsSection {
  const raw = document['navigation'],
    configuration = object(raw);
  const fields: readonly Field[] = [
    ['nodeId', 'Saved node ID'],
    ['dashboardId', 'Saved dashboard ID'],
    ['label', 'Saved label override'],
    ['iconKey', 'Saved icon override'],
    ['sortOrder', 'Saved sort order'],
    ['visible', 'Saved visibility'],
  ];
  const items = collection(
    configuration,
    'items',
    'Saved navigation items in original order',
    'navigation',
    (item, index, all) => {
      const value = object(item),
        node = uniqueTarget(document['nodes'], value['nodeId']);
      const dashboards = node
        ? object(node.raw['declaration'])['dashboards']
        : null;
      const dashboard = uniqueTarget(dashboards, value['dashboardId']);
      const current = detail(
        item,
        index,
        'navigation:item',
        `Saved menu item ${index + 1}`,
        fields,
        [
          ...(!node
            ? [
                'The saved node ID is missing or ambiguous. No node or menu target is substituted.',
              ]
            : []),
          ...(!dashboard
            ? [
                'The saved dashboard ID does not identify one dashboard under the exact saved node. No first dashboard or current source is substituted.',
              ]
            : []),
          ...(typeof value['sortOrder'] !== 'number' ||
          !Number.isSafeInteger(value['sortOrder']) ||
          typeof value['visible'] !== 'boolean'
            ? [
                'Saved ordering or visibility has an unsupported shape. No order or visibility default is supplied.',
              ]
            : []),
          ...(['label', 'iconKey'].some(
            (key) => value[key] != null && typeof value[key] !== 'string',
          )
            ? [
                'A saved override has an unsupported shape. Its exact original value is retained.',
              ]
            : []),
          ...(text(value['nodeId']) &&
          text(value['dashboardId']) &&
          all.filter(
            (candidate) =>
              object(candidate)['nodeId'] === value['nodeId'] &&
              object(candidate)['dashboardId'] === value['dashboardId'],
          ).length > 1
            ? [
                'Duplicate saved navigation target. Every original item remains in order.',
              ]
            : []),
        ],
      );
      current.fields = [
        ...current.fields,
        ...(node
          ? [
              {
                id: `navigation:item-${index}:node:index`,
                label: 'Saved node index',
                presence: 'value' as const,
                value: String(node.index),
                issues: [],
                raw: node.index,
              },
              {
                id: `navigation:item-${index}:node:target`,
                label: 'Unique saved node',
                presence: 'value' as const,
                value: display(node.raw['displayName']),
                issues: [],
                raw: copy(node.raw),
              },
            ]
          : []),
        ...(dashboard
          ? [
              {
                id: `navigation:item-${index}:dashboard:index`,
                label: 'Saved dashboard index',
                presence: 'value' as const,
                value: String(dashboard.index),
                issues: [],
                raw: dashboard.index,
              },
              {
                id: `navigation:item-${index}:dashboard:target`,
                label: 'Unique saved dashboard',
                presence: 'value' as const,
                value: display(dashboard.raw['label']),
                issues: [],
                raw: copy(dashboard.raw),
              },
            ]
          : []),
      ];
      return current;
    },
  );
  const rows =
    presence(document, 'navigation') === 'value'
      ? [
          row(
            raw,
            0,
            'navigation',
            'Saved Staff navigation',
            [
              ['iconKey', 'Saved root icon key'],
              ['items', 'Navigation items'],
            ],
            [items],
            typeof configuration['iconKey'] !== 'string'
              ? [
                  'The saved root icon has an unsupported shape. No icon is selected or rendered.',
                ]
              : [],
          ),
        ]
      : [];
  if (rows.length)
    rows[0].fields = facts(
      raw,
      [['iconKey', 'Saved root icon key']],
      'navigation-0',
    );
  return section(document, 'navigation', 'navigation', rows);
}
/** Saved configuration only. Canonical provenance is reported, never recomputed or used as runtime authority. */
export function readWorkflowPublishedWorkflowSettingsInspection(
  source: WorkflowPublishedInspectionSource,
): WorkflowPublishedWorkflowSettingsInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedWorkflowSettingsSectionId,
  ): WorkflowPublishedWorkflowSettingsSection => ({
    id,
    label: labels[id],
    state: 'unavailable',
    presence: 'absent',
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedWorkflowSettingsSectionId,
    WorkflowPublishedWorkflowSettingsSection
  > = {
    publicReferences: unavailable('publicReferences'),
    eventAutomation: unavailable('eventAutomation'),
    dependencies: unavailable('dependencies'),
    navigation: unavailable('navigation'),
  };
  const result: WorkflowPublishedWorkflowSettingsInspection = {
    context: JSON.stringify([admitted.context, source.definitionDocument]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    issues: [...admitted.issues],
    originalDocument: null,
    sections,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved workflow settings are withheld because the canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument);
  result.state = 'available';
  result.originalDocument = copy(source.definitionDocument);
  result.context = JSON.stringify([admitted.context, document]);
  sections.publicReferences = publicReferences(document);
  sections.eventAutomation = eventAutomation(document);
  sections.dependencies = dependencies(document);
  sections.navigation = navigation(document);
  return result;
}
